import {
  CollapsedGraphReference,
  conditionalFormattingFilterCacheHack,
  DEFAULT_EDGE_COLOR_ATTRIBUTES,
  getReferenceIdsFromReferenceList,
  getUrlFieldValuesByComponentId,
  getUrlFieldValuesByReferenceId,
  GraphData,
  GraphInterface,
  LabelsInterface,
  RawGraphNode2,
  RepresentationInterface,
} from '@ardoq/graph';
import {
  ConditionalFormattingState,
  RepresentationData,
} from '@ardoq/data-model';
import { getComponentIdsFromRawGraph } from 'tabview/relationshipDiagrams/viewModel$/utils';
import { filterCache } from 'collections/filterCache';
import { vizFilterGray } from '@ardoq/design-tokens';
import { isTruthy } from '@ardoq/common-helpers';

type GetGraphDataArgs = {
  viewInterface: GraphInterface & LabelsInterface & RepresentationInterface;
  componentMap: Map<string, RawGraphNode2>;
  groupMap: Map<string, RawGraphNode2>;
  referenceList: CollapsedGraphReference[];
  getRepresentationData: (componentId: string) => RepresentationData;
};

export const getGraphData = ({
  viewInterface,
  componentMap,
  groupMap,
  referenceList,
  getRepresentationData,
}: GetGraphDataArgs): GraphData => {
  const componentIds = getComponentIdsFromRawGraph({
    viewInterface,
    componentMap,
    groupMap,
  });
  const allReferenceIds = getReferenceIdsFromReferenceList(referenceList);

  const urlFieldValuesByComponentId = getUrlFieldValuesByComponentId(
    viewInterface,
    componentIds
  );

  const urlFieldValuesByReferenceId = getUrlFieldValuesByReferenceId(
    viewInterface,
    allReferenceIds
  );

  conditionalFormattingFilterCacheHack(
    viewInterface,
    componentIds.map(viewInterface.get).filter(isTruthy),
    allReferenceIds.map(viewInterface.getReference).filter(isTruthy)
  );

  const edgeColorsByReferenceModelId = new Map(
    allReferenceIds.map(referenceModelId => [
      referenceModelId,
      viewInterface.getReferenceCssColors(referenceModelId, {
        useAsBackgroundStyle: false,
      }) ?? DEFAULT_EDGE_COLOR_ATTRIBUTES,
    ])
  );

  const nodeColorsByGraphItemId = new Map(
    [...componentMap, ...groupMap].map(
      ([_, item]) =>
        [
          item.id,
          viewInterface.getComponentDisplayColor(item.modelId ?? '', {
            useAsBackgroundStyle: false,
          }),
        ] as const
    )
  );

  const conditionalFormattingState = new Map(
    [...componentMap, ...groupMap].map(
      ([_, item]): [string, ConditionalFormattingState] => {
        if (!item.modelId) {
          return [item.id, ConditionalFormattingState.NORMAL];
        }

        // Use scope data path if available
        if (viewInterface.getConditionalFormattingState) {
          return [
            item.id,
            viewInterface.getConditionalFormattingState(item.modelId),
          ];
        }

        // Fallback to BB model path
        const filterColor = filterCache.getColor(item.modelId);
        const state = !filterColor
          ? ConditionalFormattingState.NORMAL
          : filterColor === vizFilterGray
            ? ConditionalFormattingState.DIM
            : ConditionalFormattingState.FORMATTED;

        return [item.id, state];
      }
    )
  );

  const representationDataByComponentId = new Map(
    componentIds.map(componentId => [
      componentId,
      getRepresentationData(componentId),
    ])
  );

  return {
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
    edgeColorsByReferenceModelId,
    representationDataByComponentId,
  };
};
